import { z } from 'zod';
import { RoleSchema } from './verdicts.js';
/**
 * User accounts.
 *
 * `name` is the login handle and what appears against an annotation, so it is
 * the one field that cannot be blank. Everything else is contact detail: an
 * operator must never be blocked from getting an account because nobody knows
 * their postcode.
 *
 * Email is a second way to sign in rather than a replacement for the name —
 * existing accounts have none, and sessions, audit rows and annotation
 * attribution all key on the name.
 */
/** Optional free text: empty strings from a form become null, not "". */
const optionalText = (max = 200) => z
    .string()
    .max(max)
    .transform((v) => v.trim())
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const profileFields = {
    email: z.string().email('That does not look like an email address').max(200).nullable().optional(),
    firstName: optionalText(80),
    lastName: optionalText(80),
    phoneMobile: optionalText(40),
    street: optionalText(160),
    houseNumber: optionalText(20),
    postalCode: optionalText(20),
    city: optionalText(100),
    country: optionalText(100),
    companyName: optionalText(160),
    companyStreet: optionalText(160),
    companyHouseNumber: optionalText(20),
    companyPostalCode: optionalText(20),
    companyCity: optionalText(100),
    companyCountry: optionalText(100),
    companyPhone: optionalText(40),
    notes: optionalText(1000),
};
/**
 * Passwords are checked for length only.
 *
 * Composition rules (a digit, a symbol, a capital) push people towards
 * "Password1!" and away from length, which is the thing that actually makes a
 * password hard to guess. A floor of 10 with no other rules is the honest
 * trade.
 */
export const PasswordSchema = z
    .string()
    .min(10, 'Use at least 10 characters — length beats symbols')
    .max(200);
export const CreateUserSchema = z.object({
    name: z.string().min(2, 'A login name is required').max(120),
    role: RoleSchema.default('OPERATOR'),
    password: PasswordSchema.optional(),
    /** 4-6 digits for the shop-floor keypad, when that login is enabled. */
    pin: z.string().regex(/^\d{4,6}$/, 'A PIN is 4 to 6 digits').optional(),
    active: z.boolean().default(true),
    /**
     * Which customer this login belongs to.
     *
     * Required when role is CUSTOMER — that id is the only thing the portal
     * scopes to, and a portal account without one would authenticate and then
     * see nothing, or with one bug in the other direction, everything. The
     * database refuses the combination too (users_customer_role_needs_customer);
     * this is here so the answer is a 400 naming the field rather than a 500
     * quoting a constraint.
     *
     * Allowed on staff as well: an engineer can be attached to the customer they
     * look after without becoming a portal user.
     */
    customerId: z.number().int().positive().nullable().optional(),
    ...profileFields,
})
    .refine((v) => v.role !== 'CUSTOMER' || (v.customerId ?? null) !== null, {
    message: 'A CUSTOMER login must belong to a customer',
    path: ['customerId'],
});
export const UpdateUserSchema = z.object({
    name: z.string().min(2).max(120).optional(),
    role: RoleSchema.optional(),
    active: z.boolean().optional(),
    customerId: z.number().int().positive().nullable().optional(),
    ...profileFields,
});
export const SetPasswordSchema = z.object({ password: PasswordSchema });
export const SetPinSchema = z.object({ pin: z.string().regex(/^\d{4,6}$/) });
/** A display name that prefers the real one, falling back to the login handle. */
export function displayName(u) {
    const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return full || u.name;
}
//# sourceMappingURL=users.js.map